import { Injectable, signal, computed } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';

/** Serviço global para expor erros não tratados e exibir UI de fallback (evitar tela branca). */
@Injectable({ providedIn: 'root' })
export class AppErrorService {
  private readonly lastErrorSig = signal<Error | HttpErrorResponse | null>(null);

  readonly hasError = computed(() => this.lastErrorSig() != null);
  readonly lastError = computed(() => this.lastErrorSig());
  readonly friendlyMessage = computed(() => this.getFriendlyMessage(this.lastErrorSig()));

  setError(error: Error | HttpErrorResponse | null): void {
    this.lastErrorSig.set(error);
  }

  clearError(): void {
    this.lastErrorSig.set(null);
    this.removeFallbackElement();
  }

  /** Mensagem amigável para exibir ao usuário (API/banco fora, conexão, etc.). */
  getFriendlyMessage(error: Error | HttpErrorResponse | null): string {
    if (!error) return '';
    if (error instanceof HttpErrorResponse) {
      if (error.status === 0) {
        return 'Serviço indisponível. Verifique se o servidor está rodando e sua conexão.';
      }
      if (error.status >= 500) {
        return 'Erro no servidor. Tente novamente em alguns instantes.';
      }
      if (error.status === 404) {
        return 'Recurso não encontrado.';
      }
      return error.message || 'Ocorreu um erro. Tente novamente.';
    }
    return error?.message || 'Algo deu errado. Tente novamente.';
  }

  /** Indica se o erro é de rede/serviço indisponível (útil para permitir continuar em modo degradado). */
  isConnectionOrServerError(error: unknown): boolean {
    if (error instanceof HttpErrorResponse) {
      return error.status === 0 || (error.status >= 500 && error.status < 600);
    }
    return false;
  }

  private readonly FALLBACK_ID = 'app-global-error-fallback';

  /** Insere um div de fallback no body para quando o Angular quebrar antes de renderizar. */
  showFallbackElement(): void {
    if (typeof document === 'undefined') return;
    if (document.getElementById(this.FALLBACK_ID)) return;
    if (document.querySelector('.app-error-overlay')) return;
    const div = document.createElement('div');
    div.id = this.FALLBACK_ID;
    div.style.cssText = [
      'position:fixed;inset:0;z-index:99999;background:#fff;display:flex;flex-direction:column;',
      'align-items:center;justify-content:center;padding:24px;font-family:system-ui,sans-serif;text-align:center;',
    ].join('');
    div.innerHTML = [
      '<p style="font-size:18px;color:#1a1a1a;margin:0 0 8px;">Serviço temporariamente indisponível</p>',
      '<p style="font-size:14px;color:#666;margin:0 0 24px;">Verifique se o servidor e o banco de dados estão rodando.</p>',
      '<button type="button" id="app-global-error-reload" style="padding:10px 20px;font-size:14px;cursor:pointer;background:#0B0B0C;color:#fff;border:none;border-radius:8px;">Tentar novamente</button>',
    ].join('');
    document.body.appendChild(div);
    document.getElementById('app-global-error-reload')?.addEventListener('click', () => {
      window.location.reload();
    });
  }

  removeFallbackElement(): void {
    if (typeof document === 'undefined') return;
    document.getElementById(this.FALLBACK_ID)?.remove();
  }
}
